import { useTheme } from "@emotion/react";
import { Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";

import { Box, Grid } from "~/components/blocks";
import { PharmacyAppShell } from "~/components/layouts";
import { useScrollToTop } from "~/hooks/use-scroll-to-top";

import { InputPatientInfo } from "./InputPatientInfo";
import { InputQuestionnaire } from "./InputQuestionnaire";
import { SelectReservationTime } from "./SelectReservationTime";
import { Stepper, STEPS } from "./Stepper";
import { Fields, StepName, StepNameMap } from "./types";
import { UploadPrescriptions } from "./UploadPrescriptions";
import { useDefaultValues } from "./use-default-values";
import { useSendPrescriptions } from "./use-send-prescriptions";
import { validationSchema } from "./validation";

type Props = {
  pharmacyId?: string;
  currentStep?: string;
  onBack: () => void;
  onChangeStep: (stepName: StepName) => void;
};

const firstAccessStep: { [key: string]: boolean } = {
  [StepNameMap.prescription]: true,
  [StepNameMap.patientInfo]: true,
  [StepNameMap.questionnaire]: true,
};

export const SendPrescriptions = React.memo((props: Props) => {
  const { pharmacyId, currentStep, onBack, onChangeStep } = props;

  const theme = useTheme();

  const [containerRef, scrollTop] = useScrollToTop<HTMLDivElement>();
  const { isSending, error, send } = useSendPrescriptions();
  const defaultValues = useDefaultValues();

  const [firstAccess, setFirstAccess] = useState(firstAccessStep);

  const handleBack = useCallback(() => {
    const idx = STEPS.findIndex((step) => step === currentStep);
    if (idx > -1) {
      setFirstAccess((_state) => ({ ..._state, [STEPS[idx]]: false }));
    }

    onBack();
  }, [currentStep, onBack]);
  const handleNext = useCallback(() => {
    const idx = STEPS.findIndex((step) => step === currentStep);
    const nextIdx = idx === -1 ? 1 : idx + 1;
    if (nextIdx <= STEPS.length - 1) {
      onChangeStep(STEPS[nextIdx]);
    }
  }, [currentStep, onChangeStep]);

  const handleSubmit = useCallback(
    async (values: Fields) => {
      try {
        await send(values, pharmacyId);
        handleNext();
      } catch {
        // 何もしない
      }
    },
    [send, pharmacyId, handleNext],
  );

  useEffect(() => {
    if (currentStep || error) {
      scrollTop();
    }
  }, [currentStep, error, scrollTop]);

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <PharmacyAppShell
          ref={containerRef}
          title="処方箋を送信"
          onBack={handleBack}
        >
          <Grid gridTemplateRows="min-content 1fr">
            {currentStep === StepNameMap.time || currentStep === undefined ? (
              <SelectReservationTime
                pharmacyId={pharmacyId}
                formik={formik}
                onClick={handleNext}
              />
            ) : (
              <>
                {currentStep && <Stepper currentStep={currentStep} />}
                <Box margin={theme.space.l}>
                  {currentStep === StepNameMap.prescription ? (
                    <UploadPrescriptions
                      firstAccess={firstAccess["prescription"]}
                      pharmacyId={pharmacyId}
                      formik={formik}
                      onNext={handleNext}
                    />
                  ) : currentStep === StepNameMap.patientInfo ? (
                    <InputPatientInfo
                      firstAccess={firstAccess["patientInfo"]}
                      formik={formik}
                      onNext={handleNext}
                    />
                  ) : currentStep === StepNameMap.questionnaire ? (
                    <InputQuestionnaire
                      firstAccess={firstAccess["questionnaire"]}
                      sending={isSending}
                      error={error}
                      formik={formik}
                    />
                  ) : null}
                </Box>
              </>
            )}
          </Grid>
        </PharmacyAppShell>
      )}
    </Formik>
  );
});

SendPrescriptions.displayName = "SendPrescriptions";
